import type { BatteryChemistry, TemperatureDeratingPoint } from '@brt/shared-types'
import { getChemistryPreset } from '../data/chemistryPresets.js'

export function applyDoDDerating(capacityAh: number, dod: number): number {
  if (dod < 0 || dod > 1) return capacityAh
  return capacityAh * dod
}

export function applySoCDerating(capacityAh: number, soc: number): number {
  if (soc < 0) return 0
  if (soc > 1) return capacityAh
  return capacityAh * soc
}

export function applySoHDerating(capacityAh: number, soh: number): number {
  if (soh <= 0) return 0
  if (soh > 1) return capacityAh
  return capacityAh * soh
}

export function getTemperatureFactor(tempC: number, chemistry: BatteryChemistry): number {
  const preset = getChemistryPreset(chemistry)
  return interpolateTemperatureCurve(preset.deratingCurve, tempC)
}

export function applyTemperatureDerating(capacityAh: number, temperatureC: number, chemistry: BatteryChemistry): number {
  const factor = getTemperatureFactor(temperatureC, chemistry)
  return capacityAh * factor
}

export function applySelfDischarge(capacityAh: number, storageDays: number, chemistry: BatteryChemistry): number {
  if (storageDays <= 0) return capacityAh
  const preset = getChemistryPreset(chemistry)
  const dailyRate = preset.selfDischargePerDay
  const factor = Math.pow(1 - dailyRate, storageDays)
  return capacityAh * factor
}

export function solveStorageDaysForRetention(dailyRate: number, targetFraction: number): number {
  if (dailyRate <= 0) return Infinity
  const clampedFraction = Math.max(Number.MIN_VALUE, Math.min(1, targetFraction))
  return Math.log(clampedFraction) / Math.log(1 - dailyRate)
}

export function applyEfficiency(capacityAh: number, efficiency: number): number {
  if (efficiency <= 0 || efficiency > 1) return capacityAh
  return capacityAh / efficiency
}

export function applyReserveMargin(capacityAh: number, reservePercent: number): number {
  if (reservePercent < 0 || reservePercent >= 1) return capacityAh
  return capacityAh * (1 - reservePercent)
}

export type ConfidenceLevel = 'ideal' | 'adjusted' | 'approximate'

export function computeConfidenceLevel(
  activeDeratingCount: number,
  hasDutyCycle: boolean,
): { level: ConfidenceLevel; explanation: string } {
  if (activeDeratingCount === 0 && !hasDutyCycle) {
    return {
      level: 'ideal',
      explanation: 'Ideal conditions — no derating factors applied',
    }
  }

  if (activeDeratingCount <= 2 && !hasDutyCycle) {
    return {
      level: 'adjusted',
      explanation: `${activeDeratingCount} derating factor${activeDeratingCount > 1 ? 's' : ''} applied — result adjusted from ideal`,
    }
  }

  return {
    level: 'approximate',
    explanation: `${activeDeratingCount} derating factors applied${hasDutyCycle ? ' with duty-cycle averaging' : ''} — result is an approximation`,
  }
}

export function interpolateTemperatureCurve(
  curve: TemperatureDeratingPoint[],
  tempC: number,
): number {
  if (curve.length === 0) return 1.0

  const sorted = [...curve].sort((a, b) => a.tempC - b.tempC)
  const min = sorted[0]!
  const max = sorted[sorted.length - 1]!

  if (tempC <= min.tempC) return min.factor
  if (tempC >= max.tempC) return max.factor

  for (let i = 0; i < sorted.length; i++) {
    const point = sorted[i]!
    if (point.tempC === tempC) return point.factor

    const next = sorted[i + 1]
    if (next && tempC > point.tempC && tempC < next.tempC) {
      const ratio = (tempC - point.tempC) / (next.tempC - point.tempC)
      return point.factor + (next.factor - point.factor) * ratio
    }
  }

  return 1.0
}
